import { PoseData, GestureEvent, GestureType } from '../../../types';
import { isVisible } from '../../../utils/geometry';

/**
 * ArmsCrossedDetector detects when arms are crossed in front of body
 * Validates Requirements 3.3.2
 */
export class ArmsCrossedDetector {
  // Minimum crossing distance to avoid false positives from slight movements
  private static readonly MIN_CROSSING_DISTANCE = 0.05;
  
  /**
   * Detect if arms are crossed
   * Arms are considered crossed when the left wrist is to the right of the right wrist
   * @param pose Current pose data
   * @param timestamp Current timestamp
   * @returns GestureEvent if detected, null otherwise
   */
  detect(pose: PoseData, timestamp: number): GestureEvent | null {
    if (!pose.detected) return null;
    
    const { leftWrist, rightWrist, leftShoulder, rightShoulder, leftHip, rightHip } = pose.landmarks;
    
    // Check if all required landmarks are visible
    if (!isVisible(leftWrist) || !isVisible(rightWrist) ||
        !isVisible(leftShoulder) || !isVisible(rightShoulder)) {
      return null;
    }
    
    // Arms are crossed if left wrist is to the right of right wrist
    const crossingDistance = leftWrist.x - rightWrist.x;
    const armsCrossed = crossingDistance > ArmsCrossedDetector.MIN_CROSSING_DISTANCE;
    
    if (!armsCrossed) {
      return null;
    }
    
    // Optional: Check that wrists are roughly at chest level (between shoulders and hips)
    // This helps filter out false positives from other arm positions
    const shoulderY = (leftShoulder.y + rightShoulder.y) / 2;
    const hipY = isVisible(leftHip) && isVisible(rightHip) 
      ? (leftHip.y + rightHip.y) / 2 
      : shoulderY + 0.3; // Fallback if hips not visible
    
    const wristY = (leftWrist.y + rightWrist.y) / 2;
    const atChestLevel = wristY > shoulderY && wristY < hipY;
    
    if (!atChestLevel) {
      return null;
    }
    
    // Calculate confidence based on how much the arms are crossed
    // More crossing = higher confidence
    const maxCrossingDistance = 0.3; // Maximum expected crossing distance
    const normalizedCrossing = Math.min(crossingDistance / maxCrossingDistance, 1.0);
    const confidence = Math.max(0.5, normalizedCrossing); // Minimum 0.5 confidence
    
    return {
      type: GestureType.ARMS_CROSSED,
      confidence,
      timestamp
    };
  }
}
